import React, { useCallback, useRef, useState } from 'react';

import type { SmartLinkResponse } from '@atlaskit/linking-types/smart-link';
import { token } from '@atlaskit/tokens';

import { extractSmartLinkPreviewImage } from '../../../extractors/flexible/extract-smart-link-preview-image';
import { default as PreviewBlock } from '../../FlexibleCard/components/blocks/preview-block';

type ImagePreviewProps = {
	fallbackElementHeight: number;
	response?: SmartLinkResponse;
};

const ImagePreview = ({
	fallbackElementHeight,
	response,
}: ImagePreviewProps): React.JSX.Element | null => {
	const transitionStarted = useRef<boolean>(false);
	const previewBlockRef = useRef<HTMLDivElement>(null);
	const [showPreview, setShowPreview] = useState<boolean>(true);
	const [dynamicStyles, setDynamicStyles] = useState<React.CSSProperties>({});

	// Set Preview to a fixed height to enable transitions
	const onPreviewRender = useCallback(() => {
		if (previewBlockRef.current) {
			const previewHeight = previewBlockRef.current?.getBoundingClientRect().height;
			setDynamicStyles({
				borderTopLeftRadius: token('radius.large'),
				borderTopRightRadius: token('radius.large'),
				...(previewHeight ? { height: `${previewHeight}px` } : {}),
			});
		}
	}, []);

	// On error set Preview to Fallback height with transition
	const onPreviewError = useCallback(() => {
		if (transitionStarted.current === false) {
			setDynamicStyles({
				transition: 'height 300ms ease-in-out',
				height: `${fallbackElementHeight}px`,
			});
			transitionStarted.current = true;
		}
	}, [fallbackElementHeight]);

	const onPreviewTransitionEnd = useCallback(() => {
		setShowPreview(false);
	}, []);

	return showPreview && extractSmartLinkPreviewImage(response) ? (
		<PreviewBlock
			onError={onPreviewError}
			ignoreContainerPadding={true}
			onTransitionEnd={onPreviewTransitionEnd}
			blockRef={previewBlockRef}
			onRender={onPreviewRender}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={dynamicStyles}
		/>
	) : null;
};

export default ImagePreview;
